import { z } from 'zod';
import { ResumeEducationItemSchema } from './education.js';

// ── Resume status ──────────────────────────────────────────────────────────────

export const ResumeStatusSchema = z.enum([
  'pending',
  'generating',
  'completed',
  'failed',
]);

export type ResumeStatus = z.infer<typeof ResumeStatusSchema>;

// ── Resume document structure ──────────────────────────────────────────────────
// Stored as jsonb in the document column.

/** A single bullet in the resume, referencing the source capability */
export const ResumeBulletSchema = z.object({
  /** UUID of the source capability — null for synthesized bullets */
  capabilityId: z.string().uuid().optional(),
  text: z.string(),
  /** 0.0–1.0 relevance score assigned by the LLM */
  confidenceScore: z.number().min(0).max(1).optional(),
  /** LLM's explanation of why this bullet was selected or how it was synthesized */
  reasoning: z.string().optional(),
  /** Verbatim text of the job requirement this bullet was synthesized to address (synthesis only) */
  targetRequirement: z.string().optional(),
});

export type ResumeBullet = z.infer<typeof ResumeBulletSchema>;

/**
 * One project under a title — corresponds 1:1 to an `experiences` row.
 * The project carries a stable `experienceId` (used by the bullet-edit UI to
 * target the right experience) and an optional `role` label.
 */
export const ResumeProjectItemSchema = z.object({
  /** UUID of the experiences row — the project's stable identity */
  experienceId: z.string().uuid(),
  /** Optional project name from the experience row (e.g. "Payment Gateway Migration") */
  project: z.string().optional(),
  bullets: z.array(ResumeBulletSchema),
});

export type ResumeProjectItem = z.infer<typeof ResumeProjectItemSchema>;

/** One job in the experience section — grouped at the Title level (one official job title per entry) */
export const ResumeExperienceItemSchema = z.object({
  /** UUID of the titles row — primary grouping key */
  titleId: z.string().uuid(),
  /** UUID of the tenures row — used by the collapse transform to group titles under one company */
  tenureId: z.string().uuid(),
  /** Official job title (e.g. "Senior Software Engineer") — the primary heading */
  title: z.string(),
  /** Company / organisation name, denormalised from the parent tenure */
  company: z.string(),
  /** Human-readable date range derived from the title row (e.g. "Jan 2022 – Mar 2024") */
  dateRange: z.string().optional(),
  /** Raw ISO date strings from the title row — used by applyDisplayOptions for span calculation without string parsing */
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  /** Raw ISO date strings from the parent tenure row — used for full-tenure date span when titles are collapsed */
  tenureStartDate: z.string().optional(),
  tenureEndDate: z.string().optional(),
  /** Projects under this title; each project carries its own role + bullets */
  projects: z.array(ResumeProjectItemSchema),
});

export type ResumeExperienceItem = z.infer<typeof ResumeExperienceItemSchema>;

/** Resume section types */
export const ResumeSectionTypeSchema = z.enum([
  'experience',
  'skills',
  'education',
  'certifications',
  'custom',
]);

export type ResumeSectionType = z.infer<typeof ResumeSectionTypeSchema>;

/** A generic section in the resume */
export const ResumeSectionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('experience'),
    title: z.string().default('Work Experience'),
    items: z.array(ResumeExperienceItemSchema),
  }),
  z.object({
    type: z.literal('skills'),
    title: z.string().default('Skills'),
    /** Flat list of skill strings drawn from the matched capabilities */
    items: z.array(z.string()),
  }),
  z.object({
    type: z.literal('education'),
    title: z.string().default('Education'),
    /** Structured education entries from the user's education table */
    items: z.array(ResumeEducationItemSchema),
  }),
  z.object({
    type: z.literal('certifications'),
    title: z.string().default('Certifications'),
    items: z.array(z.string()),
  }),
  z.object({
    type: z.literal('custom'),
    title: z.string(),
    /** Custom instructions the user provided for this section */
    instructions: z.string().optional(),
    /** Bullets selected / generated for this section */
    bullets: z.array(ResumeBulletSchema),
  }),
]);

export type ResumeSection = z.infer<typeof ResumeSectionSchema>;

/** The full structured resume document */
export const ResumeDocumentSchema = z.object({
  /** Contact / header information */
  header: z.object({
    name: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    location: z.string().optional(),
    linkedin: z.string().optional(),
    github: z.string().optional(),
    website: z.string().optional(),
  }),
  /** Optional 2–3 sentence tailored career summary */
  summary: z.string().optional(),
  /** Ordered sections of the resume */
  sections: z.array(ResumeSectionSchema),
  /** Optional skill groups; if present, skills are organized by bucket in resume rendering */
  skillGroups: z.array(z.object({
    name: z.string().optional(),  // absent = unlabeled / "Other"
    skills: z.array(z.string()),
  })).optional(),
});

export type ResumeDocument = z.infer<typeof ResumeDocumentSchema>;

// ── Confidence summary ─────────────────────────────────────────────────────────

export const ConfidenceSummarySchema = z.object({
  /** What the candidate addresses well for this posting */
  strengths: z.array(z.string()),
  /** Requirements the candidate's profile does not cover */
  gaps: z.array(z.string()),
  /** Overall match score 0–100 */
  overallScore: z.number().int().min(0).max(100).optional(),
});

export type ConfidenceSummary = z.infer<typeof ConfidenceSummarySchema>;

// ── Ideal Candidate Profile (ICP) Scoring and Evaluation ────────────────────
// ICP = Ideal Candidate Profile: a structured rubric scoring candidates against
// job posting criteria on a 0-5 scale per criterion, with weighted importance.

export const IcpScoringRowSchema = z.object({
  criterionNumber: z.number(),
  criterion: z.string(),
  weight: z.number(), // 3=primary, 2=secondary, 1=tertiary importance
  score: z.number().min(0).max(5), // 0-5: 5=exceptional, 4=strong, 3=adequate, 2=partial, 1=weak, 0=missing
  evidence: z.string(), // Direct quote or specific reference from candidate profile
});

export type IcpScoringRow = z.infer<typeof IcpScoringRowSchema>;

export const PerspectiveBriefingsSchema = z.object({
  optimistic: z.string(),
  balanced: z.string(),
  critical: z.string(),
});

export type PerspectiveBriefings = z.infer<typeof PerspectiveBriefingsSchema>;

export const ResumeEvaluationSchema = z.object({
  icpScoring: z.array(IcpScoringRowSchema).optional(), // Ideal Candidate Profile scoring breakdown
  totalScore: z.string().optional(), // e.g., "56 / 72"
  fitPercentage: z.number().int().min(0).max(100).optional(), // Calculated from ICP scoring: (totalScore / maxPossible) * 100
  perspectiveBriefings: PerspectiveBriefingsSchema.optional(), // Recruiter perspectives on fit
});

export type ResumeEvaluation = z.infer<typeof ResumeEvaluationSchema>;

// ── Display options (per-resume presentation preferences) ─────────────────────
// Stored in resumes.display_options (jsonb). These are pure rendering hints that
// never mutate the canonical ResumeDocument. The collapse transform is applied
// at render time (both in the frontend preview and in the export renderers).

export const ResumeDisplayOptionsSchema = z.object({
  /**
   * Tenure IDs whose title sections should be collapsed into a single entry
   * under the most recent title, spanning the full tenure date range.
   */
  collapsedTenures: z.array(z.string().uuid()),
});

export type ResumeDisplayOptions = z.infer<typeof ResumeDisplayOptionsSchema>;

export const UpdateResumeDisplayOptionsSchema = z.object({
  displayOptions: ResumeDisplayOptionsSchema,
});

export type UpdateResumeDisplayOptions = z.infer<typeof UpdateResumeDisplayOptionsSchema>;

// ── Custom section spec (user input at generation time) ───────────────────────

export const CustomSectionSpecSchema = z.object({
  title: z.string().min(1),
  instructions: z.string().optional(),
});

export type CustomSectionSpec = z.infer<typeof CustomSectionSpecSchema>;

// ── Full resume (server → client) ─────────────────────────────────────────────

export const ResumeSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  postingId: z.string().uuid(),
  status: ResumeStatusSchema,
  document: ResumeDocumentSchema.nullable(),
  confidenceSummary: ConfidenceSummarySchema.nullable(),
  customSections: z.array(CustomSectionSpecSchema).nullable(),
  errorMessage: z.string().nullable(),
  aiRunId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Denormalized from the related posting (for list views) */
  postingTitle: z.string().nullable().optional(),
  postingCompany: z.string().nullable().optional(),
  postingUrl: z.string().optional(),
  /** Per-resume presentation preferences (collapse/expand tenure title groups) */
  displayOptions: ResumeDisplayOptionsSchema.nullable(),
  /** ICP scoring, fit percentage, and recruiter perspectives */
  evaluation: ResumeEvaluationSchema.optional(),
});

export type Resume = z.infer<typeof ResumeSchema>;

// ── Create resume request (client → server) ───────────────────────────────────

export const CreateResumeSchema = z.object({
  postingId: z.string().uuid('Must be a valid posting ID'),
  customSections: z.array(CustomSectionSpecSchema).optional(),
  /** 'standard' = verbatim bullet selection; 'synthesize' = LLM rewrites bullets to target job requirements */
  strategy: z.enum(['standard', 'synthesize']).default('standard'),
});

export type CreateResume = z.infer<typeof CreateResumeSchema>;

// ── Synthesis types ───────────────────────────────────────────────────────────
// Used by the synthesis pass in the resume-generation worker. The LLM returns
// synthesized bullets that combine multiple source capabilities into new text.

export const SynthesizedBulletSchema = z.object({
  experienceId: z.string().uuid(),
  text: z.string(),
  reasoning: z.string(),
  /** IDs of the source capabilities that were combined to produce this bullet */
  sourceCapabilityIds: z.array(z.string().uuid()),
  /** Verbatim text of the job requirement this bullet was synthesized to address */
  targetRequirement: z.string(),
  /** 0.0–1.0: how well this bullet addresses the targeted requirement */
  confidenceScore: z.number().min(0).max(1),
});

export type SynthesizedBullet = z.infer<typeof SynthesizedBulletSchema>;

export const SynthesisResponseSchema = z.object({
  bullets: z.array(SynthesizedBulletSchema),
});

export type SynthesisResponse = z.infer<typeof SynthesisResponseSchema>;

// ── Reorder resume bullets request (client -> server) ────────────────────────

export const ReorderResumeBulletsSchema = z.object({
  sectionIndex: z.number().int().min(0),
  itemIndex: z.number().int().min(0),
  projectIndex: z.number().int().min(0),
  bulletOrder: z.array(z.string().uuid()),
});

export type ReorderResumeBullets = z.infer<typeof ReorderResumeBulletsSchema>;

// ── Update resume bullet text request (client -> server) ─────────────────────

export const UpdateResumeBulletTextSchema = z.object({
  sectionIndex: z.number().int().min(0),
  itemIndex: z.number().int().min(0),
  projectIndex: z.number().int().min(0),
  capabilityId: z.string().uuid(),
  text: z.string().min(1),
});

export type UpdateResumeBulletText = z.infer<typeof UpdateResumeBulletTextSchema>;

// ── Resume bullet row (resume_bullets table → client) ─────────────────────────
// Represents a mutable per-resume snapshot of a bullet, independent from the
// source capability. Rows are created when a resume is generated or built manually.

export const ResumeBulletRowSchema = z.object({
  id: z.string().uuid(),
  resumeId: z.string().uuid(),
  capabilityId: z.string().uuid().nullable(),
  text: z.string(),
  confidenceScore: z.number().min(0).max(1).nullable(),
  reasoning: z.string().nullable(),
  isIncluded: z.boolean(),
  sortOrder: z.number().int(),
  tenureId: z.string().uuid().nullable(),
  titleId: z.string().uuid().nullable(),
  experienceId: z.string().uuid().nullable(),
  section: z.string(),
});

export type ResumeBulletRow = z.infer<typeof ResumeBulletRowSchema>;

// ── Resume bullet mutations (client -> server) ────────────────────────────────

export const UpdateResumeBulletRowSchema = z.object({
  text: z.string().min(1),
});

export type UpdateResumeBulletRow = z.infer<typeof UpdateResumeBulletRowSchema>;

export const AddResumeBulletSchema = z.object({
  capabilityId: z.string().uuid(),
});

export type AddResumeBullet = z.infer<typeof AddResumeBulletSchema>;

// ── Available capability for selection mode (server → client) ─────────────────
// A user capability annotated with whether it is currently included in a resume,
// plus the resume_bullets.id needed to toggle it off.

export const AvailableCapabilitySchema = z.object({
  id: z.string().uuid(),
  body: z.string(),
  skills: z.array(z.string()),
  experienceId: z.string().uuid(),
  titleId: z.string().uuid(),
  tenureId: z.string().uuid(),
  company: z.string(),
  title: z.string(),
  project: z.string().nullable(),
  sortOrder: z.number().int(),
  isIncluded: z.boolean(),
  resumeBulletId: z.string().uuid().nullable(),
});

export type AvailableCapability = z.infer<typeof AvailableCapabilitySchema>;
